<template>
  <div v-if="show" class="modal-overlay" @click.self="close">
    <div class="modal-content">
      <div class="modal-header">
        <h3>Order Details</h3>
        <button @click="close" class="close-btn">
          <i class="fa-solid fa-xmark"></i>
        </button>
      </div>

      <div class="modal-body">
        <div v-if="loading" class="loading-spinner">
          <i class="fa fa-spinner fa-spin"></i> Loading...
        </div>

        <div v-else-if="orderData" class="order-details">
          <div class="order-info">
            <div class="info-item">
              <strong>Order No:</strong> {{ orderData.order?.increment_id || 'N/A' }}
            </div>
            <div class="info-item">
              <strong>Order Date:</strong> {{ formatDate(orderData.order?.created_at) }}
            </div>
            <div class="info-item">
              <strong>Dealer Name:</strong> {{ orderData.order?.business_name || 'N/A' }}
            </div>
          </div>

          <div class="items-section">
            <h4>Order Items</h4>
            <table class="items-table">
              <thead>
              <tr>
                <th>Item Name</th>
                <th>Quantity</th>
              </tr>
              </thead>
              <tbody>
              <tr v-for="(item, index) in orderData.items" :key="index">
                <td>{{ item.item_name }}</td>
                <td>{{ item.qty }}</td>
              </tr>
              <tr v-if="!orderData.items || orderData.items.length === 0">
                <td colspan="2" class="no-items">No items found</td>
              </tr>
              </tbody>
            </table>
          </div>
        </div>

        <div v-else class="error-message">
          <p>No order details found.</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { fetchOrderDetails } from '@/services/dataService';

export default {
  name: 'ViewOrderDetails',
  props: {
    show: {
      type: Boolean,
      default: false
    },
    orderId: {
      type: [String, Number],
      default: null
    }
  },
  data() {
    return {
      loading: false,
      orderData: null
    }
  },
  watch: {
    show(newVal) {
      if (newVal && this.orderId) {
        this.loadOrderDetails();
      } else {
        this.orderData = null;
      }
    },
    orderId() {
      if (this.show && this.orderId) {
        this.loadOrderDetails();
      }
    }
  },
  methods: {
    async loadOrderDetails() {
      if (!this.orderId) return;

      this.loading = true;
      try {
        const token = localStorage.getItem('token_set') || '';
        const response = await fetchOrderDetails(this.orderId, token);

        if (response.data && response.data.data) {
          this.orderData = response.data.data;
        } else {
          this.orderData = null;
        }
      } catch (error) {
        console.error('Error fetching order details:', error);
        this.orderData = null;
      } finally {
        this.loading = false;
      }
    },

    formatDate(dateString) {
      if (!dateString) return 'N/A';
      const options = { year: 'numeric', month: 'long', day: 'numeric' };
      return new Date(dateString).toLocaleDateString(undefined, options);
    },

    close() {
      this.$emit('close');
    }
  }
}
</script>

<style scoped lang="scss">
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
}

.modal-content {
  background-color: white;
  border-radius: 8px;
  width: 600px;
  max-width: 90%;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.3);
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 20px 0 20px;
  border-bottom: 1px solid #e9ecef;
  margin-bottom: 0;
}

.modal-header h3 {
  margin: 0;
  font-size: 1.25rem;
  font-weight: 600;
  color: #333;
}

.close-btn {
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: #6c757d;
  padding: 0;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    color: #333;
    background-color: #f8f9fa;
    border-radius: 50%;
  }
}

.modal-body {
  padding: 20px;
}

.loading-spinner {
  text-align: center;
  padding: 40px 20px;
  color: #6c757d;

  .fa-spinner {
    margin-right: 8px;
  }
}

.order-details {
  .order-info {
    background-color: #f8f9fa;
    border-radius: 6px;
    padding: 15px;
    margin-bottom: 20px;

    .info-item {
      margin-bottom: 8px;

      &:last-child {
        margin-bottom: 0;
      }

      strong {
        color: #495057;
        font-weight: 600;
        margin-right: 5px;
      }
    }
  }

  .items-section {
    h4 {
      margin: 0 0 15px 0;
      color: #333;
      font-size: 1.1rem;
    }
  }
}

.items-table {
  width: 100%;
  border-collapse: collapse;

  th, td {
    padding: 12px;
    text-align: left;
    border-bottom: 1px solid #dee2e6;
  }

  th {
    background-color: #f8f9fa;
    font-weight: 600;
    color: #495057;
  }

  td {
    background-color: #ffffff;
  }

  .no-items {
    text-align: center;
    color: #6c757d;
    font-style: italic;
  }
}

.error-message {
  text-align: center;
  padding: 40px 20px;
  color: #dc3545;
}

.btn {
  padding: 8px 16px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-weight: 500;
  transition: all 0.2s ease;

  &.btn-secondary {
    background-color: #6c757d;
    color: white;

    &:hover {
      background-color: #5a6268;
    }
  }
}

/* Responsive adjustments */
@media (max-width: 576px) {
  .modal-content {
    width: 95%;
    margin: 20px;
  }

  .modal-header {
    padding: 15px 15px 0 15px;
  }

  .modal-body {
    padding: 15px;
  }

  .modal-footer {
    padding: 15px;
  }

  .items-table {
    th, td {
      padding: 8px;
    }
  }
}
</style>